import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { publicApi } from '../lib/api';
import { useAuthStore } from '../store/useStore';
import { decodeHtmlEntities } from '../utils/decodeHtml';
import { formatAge, stamp } from '../utils/age';

interface PublicShare {
  id: string;
  note: string | null;
  isPublic: boolean;
  createdAt: string;
  user: {
    id: string;
    username: string;
    displayName: string | null;
    avatar: string | null;
  };
  article: {
    id: string;
    title: string;
    url: string;
    excerpt: string | null;
    content: string;
    imageUrl: string | null;
    publishedAt: string | null;
    feed: {
      id: string;
      title: string;
      siteUrl: string | null;
    };
    _count: {
      comments: number;
      shares: number;
      likes: number;
    };
  };
}

interface PublicPost {
  id: string;
  title: string;
  summary: string | null;
  status: string;
  isPublic: boolean;
  publishedAt: string | null;
  triggerArticle: {
    id: string;
    title: string;
    url: string;
    excerpt: string | null;
    imageUrl: string | null;
    feed: {
      id: string;
      title: string;
    };
  };
  authors: Array<{
    id: string;
    role: string;
    user: {
      id: string;
      username: string;
      displayName: string | null;
      avatar: string | null;
    };
  }>;
  sections: Array<{
    id: string;
    authorId: string;
    order: number;
  }>;
}

interface InstanceInfo {
  name: string;
  description: string;
  allowRegistration: boolean;
  version: string;
  userCount: number;
}

interface PublicUser {
  id: string;
  username: string;
  displayName: string | null;
  avatar: string | null;
  bio: string | null;
  publicShareCount: number;
}

export default function PublicLanding() {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [instanceInfo, setInstanceInfo] = useState<InstanceInfo | null>(null);
  const [shares, setShares] = useState<PublicShare[]>([]);
  const [posts, setPosts] = useState<PublicPost[]>([]);
  const [publicUsers, setPublicUsers] = useState<PublicUser[]>([]);
  const [loadingShares, setLoadingShares] = useState(true);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [sharesHasMore, setSharesHasMore] = useState(false);
  const [postsHasMore, setPostsHasMore] = useState(false);
  const [expandedArticle, setExpandedArticle] = useState<string | null>(null);

  useEffect(() => {
    async function loadData() {
      try {
        const instance = await publicApi.getInstance();
        setInstanceInfo(instance);

        const sharesData = await publicApi.getPublicShares({ limit: 10 });
        setShares(sharesData.shares);
        setSharesHasMore(sharesData.pagination.hasMore);
        setLoadingShares(false);

        const postsData = await publicApi.getPublicPosts({ limit: 5 });
        setPosts(postsData.posts);
        setPostsHasMore(postsData.pagination.hasMore);
        setLoadingPosts(false);

        const usersData = await publicApi.getPublicUsers();
        setPublicUsers(usersData.users);
      } catch (error) {
        console.error('Failed to load public content:', error);
        setLoadingShares(false);
        setLoadingPosts(false);
      }
    }

    loadData();
  }, []);

  async function loadMoreShares() {
    try {
      const data = await publicApi.getPublicShares({ limit: 10, offset: shares.length });
      setShares([...shares, ...data.shares]);
      setSharesHasMore(data.pagination.hasMore);
    } catch (error) {
      console.error('Failed to load more shares:', error);
    }
  }

  async function loadMorePosts() {
    try {
      const data = await publicApi.getPublicPosts({ limit: 5, offset: posts.length });
      setPosts([...posts, ...data.posts]);
      setPostsHasMore(data.pagination.hasMore);
    } catch (error) {
      console.error('Failed to load more posts:', error);
    }
  }

  const memberCount = instanceInfo?.userCount ?? 0;

  return (
    <div className="min-h-screen bg-canvas text-ink">
      {/* Top rule: wordmark and account actions */}
      <header className="sticky top-0 z-10 bg-canvas border-b border-edge">
        <div className="flex items-stretch h-12">
          <Link to="/" className="wordmark text-xl flex items-center px-4 lg:px-12 border-r border-edge">
            ou<em>r</em>ss
          </Link>
          <span className="flex-1" />
          {isAuthenticated ? (
            <Link to="/read" className="tool border-l border-edge border-r-0 text-ink">
              Open reader →
            </Link>
          ) : (
            <>
              <Link to="/login" className="tool border-l border-edge">
                Sign in
              </Link>
              {instanceInfo?.allowRegistration && (
                <Link to="/register" className="tool border-r-0 bg-ink text-canvas hover:bg-ink-secondary hover:text-canvas">
                  Join
                </Link>
              )}
            </>
          )}
        </div>
      </header>

      {/* Masthead: instance name as display type, mono kicker beneath */}
      <section className="border-b border-edge px-4 lg:px-12 py-10 lg:py-14">
        <h1 className="text-[2.5rem] sm:text-[3.5rem] lg:text-[4.5rem] leading-[0.95] tracking-[-0.03em] mb-4">
          {instanceInfo?.name || 'OurSS'}
        </h1>
        <div className="kicker text-ink mb-4">
          Self-hosted · {memberCount} {memberCount === 1 ? 'member' : 'members'} ·{' '}
          <a href="/feed.xml" className="hover:text-accent-600">RSS</a>
        </div>
        <p className="text-lg text-ink-secondary max-w-prose">
          {instanceInfo?.description || 'A small group of people reading the web together, sharing what holds up.'}
        </p>
      </section>

      <div className="grid lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        {/* Public shares */}
        <section className="lg:border-r border-edge">
          <div className="flex items-center h-10 px-4 lg:px-12 border-b border-edge">
            <span className="kicker text-ink">Public shares</span>
          </div>

          {loadingShares ? (
            <div className="px-4 lg:px-12 py-8 kicker">Loading…</div>
          ) : shares.length === 0 ? (
            <div className="px-4 lg:px-12 py-8">
              <div className="kicker mb-1">Empty</div>
              <p className="text-ink-secondary">Nothing shared publicly yet.</p>
            </div>
          ) : (
            <div>
              {shares.map((share, index) => (
                <article key={share.id} className="grid grid-cols-[3.5rem_1fr] lg:grid-cols-[6rem_1fr] border-b border-edge-subtle">
                  <div className="pl-3 lg:pl-12 pr-2 py-4 border-r border-edge-subtle">
                    <span className="stamp">{stamp(index)}</span>
                  </div>
                  <div className="min-w-0 pl-4 pr-4 lg:pr-8 py-3 max-w-list">
                    <div className="kicker mb-0.5">
                      <Link to={`/u/${share.user.username}`} className="text-ink hover:text-accent-600">
                        {share.user.displayName || share.user.username}
                      </Link>
                      {' · '}{formatAge(share.createdAt)}
                      {' · '}{share.article.feed.title}
                    </div>

                    <h2 className="font-sans text-[18px] font-semibold leading-snug tracking-normal mb-1">
                      <a href={share.article.url} target="_blank" rel="noopener noreferrer" className="hover:text-accent-600">
                        {decodeHtmlEntities(share.article.title)}
                      </a>
                    </h2>

                    {share.note && (
                      <blockquote className="my-2 pl-3 border-l-2 border-ink text-[15px] text-ink-secondary leading-relaxed">
                        {share.note}
                      </blockquote>
                    )}

                    {share.article.excerpt && expandedArticle !== share.id && (
                      <p className="text-sm text-ink-secondary line-clamp-2">{share.article.excerpt}</p>
                    )}

                    {expandedArticle === share.id && (
                      <div
                        className="article-content mt-3"
                        dangerouslySetInnerHTML={{ __html: share.article.content }}
                      />
                    )}

                    <div className="flex items-center gap-4 mt-2 kicker">
                      <a
                        href={share.article.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 hover:text-ink"
                      >
                        Source <ExternalLink className="w-3 h-3" />
                      </a>
                      {share.article._count.comments > 0 && (
                        <span>{share.article._count.comments} {share.article._count.comments === 1 ? 'comment' : 'comments'}</span>
                      )}
                      {share.article._count.shares > 1 && (
                        <span>{share.article._count.shares} shares</span>
                      )}
                      {share.article.content && (
                        <button
                          onClick={() => setExpandedArticle(expandedArticle === share.id ? null : share.id)}
                          className="hover:text-ink ml-auto"
                        >
                          {expandedArticle === share.id ? 'Show less' : 'Read here'}
                        </button>
                      )}
                    </div>
                  </div>
                </article>
              ))}

              {sharesHasMore && (
                <button onClick={loadMoreShares} className="tool w-full h-12 pl-4 lg:pl-12 border-r-0 border-b border-edge-subtle">
                  More shares ↓
                </button>
              )}
            </div>
          )}
        </section>

        {/* Right column */}
        <aside>
          <div className="flex items-center h-10 px-4 lg:px-8 border-b border-edge">
            <span className="kicker text-ink">Collaborative posts · beta</span>
          </div>

          {loadingPosts ? (
            <div className="px-4 lg:px-8 py-6 kicker">Loading…</div>
          ) : posts.length === 0 ? (
            <div className="px-4 lg:px-8 py-6 border-b border-edge">
              <p className="text-sm text-ink-muted">None published yet.</p>
            </div>
          ) : (
            <div className="border-b border-edge">
              {posts.map((post) => (
                <article
                  key={post.id}
                  className="px-4 lg:px-8 py-4 border-b border-edge-subtle last:border-b-0 cursor-pointer hover:bg-surface-sunken"
                  onClick={() => navigate(`/post/${post.id}`)}
                >
                  <div className="kicker mb-0.5">
                    {post.authors.length} {post.authors.length === 1 ? 'author' : 'authors'}
                    {post.publishedAt && <> · {formatAge(post.publishedAt)}</>}
                  </div>
                  <h3 className="font-sans text-[16px] font-semibold leading-snug tracking-normal mb-1">{post.title}</h3>
                  {post.summary && (
                    <p className="text-sm text-ink-secondary line-clamp-2">{post.summary}</p>
                  )}
                  <div className="kicker mt-1 normal-case tracking-normal">
                    Re: {post.triggerArticle.title}
                  </div>
                </article>
              ))}
              {postsHasMore && (
                <button onClick={loadMorePosts} className="tool w-full h-10 pl-4 lg:pl-8 border-r-0">
                  More posts ↓
                </button>
              )}
            </div>
          )}

          {publicUsers.length > 0 && (
            <>
              <div className="flex items-center h-10 px-4 lg:px-8 border-b border-edge">
                <span className="kicker text-ink">Members</span>
              </div>
              <div className="border-b border-edge">
                {publicUsers.map((member) => (
                  <Link
                    key={member.id}
                    to={`/u/${member.username}`}
                    className="flex items-center gap-3 px-4 lg:px-8 py-2.5 border-b border-edge-subtle last:border-b-0 hover:bg-surface-sunken"
                  >
                    {member.avatar ? (
                      <img src={member.avatar} alt={member.username} className="w-8 h-8 rounded-full object-cover avatar-duotone" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-ink text-canvas flex items-center justify-center font-mono text-xs">
                        {member.username[0].toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-ink truncate">
                        {member.displayName || member.username}
                      </p>
                      <p className="kicker">
                        {member.publicShareCount} public {member.publicShareCount === 1 ? 'share' : 'shares'}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}

          {!isAuthenticated && (
            <div className="px-4 lg:px-8 py-6">
              <div className="kicker mb-2">Join</div>
              <p className="text-sm text-ink-secondary mb-4">
                Read your feeds, share the good ones with people you know, get a Sunday digest.
              </p>
              <Link
                to={instanceInfo?.allowRegistration ? '/register' : '/login'}
                className="btn btn-accent"
              >
                {instanceInfo?.allowRegistration ? 'Create an account' : 'Sign in'}
              </Link>
            </div>
          )}
        </aside>
      </div>

      <footer className="border-t border-edge px-4 lg:px-12 py-6 flex flex-wrap items-center gap-x-6 gap-y-2">
        <a href="/feed.xml" className="kicker hover:text-ink">RSS feed</a>
        <span className="kicker">Powered by ourss · self-hosted social RSS</span>
      </footer>
    </div>
  );
}
